/**
 * API pública de la telemetría 3.1 del funnel. Se importa desde cualquier sitio
 * (pantallas, contextos, servicios):
 *
 *   import { track, trackSubmissionError, tracker } from "../helpers/Tracking";
 *   track("quote_viewed", { capital, price, period, coverage });
 *   tracker.redirecting("3ds"); // justo antes de mandar al cliente fuera
 *
 * El `TrackingProvider` (src/context/Tracking.context.tsx) alimenta al tracker
 * con el lead, el paso y el estado del lead, e instala las escuchas del
 * navegador. Se envía en todos los entornos (campo `env`); sin
 * `REACT_APP_EVENTS_API` no sale nada. Contrato: contract/front-event-v3.catalog.json.
 */
import { getCookie } from "../../hooks/getCookie";
import { resolveAttributionValue } from "../../utils/attributionResolve";
import { analyticsConsentState } from "../../utils/consentGatedCookies";
import { entryContext } from "./entry";
import {
  CtaId,
  DeviceType,
  EventName,
  EventPayloads,
  HelpChannel,
  LeadVia,
  LoginMethod,
  PaymentMethod,
  PaymentStage,
  Stack,
  SubmissionErrorType,
  ThreeDsResult,
  ValidationErrorType,
} from "./events";
import { IdentityManager, newClientEventId } from "./IdentityManager";
import { navigationTypeOf } from "./session";
import { marketFromLocale } from "./steps";
import { envFromEnviroment, Tracker, TrackOptions } from "./tracker";
import { eventsEndpoint, TrackingTransport } from "./Transport";

export type {
  EventName,
  EventPayloads,
  Stack,
  PaymentMethod,
  PaymentStage,
  CtaId,
  LeadVia,
  LoginMethod,
  ExitDestination,
} from "./events";
export type { StepId } from "./steps";
export { cleanReferrer } from "./entry";

const getDeviceType = (): DeviceType => {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
};

const getEntryContext = () =>
  entryContext({
    pathname: typeof window !== "undefined" ? window.location.pathname : "/",
    search: typeof window !== "undefined" ? window.location.search : "",
    referrer: typeof document !== "undefined" ? document.referrer : null,
    resolve: resolveAttributionValue,
    cookie: (name) => getCookie(name) as string | null,
  });

const storage = (kind: "sessionStorage" | "localStorage"): Storage | null => {
  try {
    return typeof window !== "undefined" ? window[kind] : null;
  } catch {
    return null;
  }
};

const env = envFromEnviroment(process.env.REACT_APP_ENVIROMENT);

export const tracker = new Tracker({
  transport: new TrackingTransport({
    endpoint: eventsEndpoint(),
    log:
      env === "dev"
        ? // eslint-disable-next-line no-console
          (event) => console.debug(`[tracking 3.1] ${event.event_name}`, event)
        : undefined,
  }),
  identity: IdentityManager,
  env,
  market: marketFromLocale(process.env.REACT_APP_DOMAIN_LOCALE),
  appVersion: process.env.REACT_APP_VERSION || null,
  getAbVariant: () => getCookie("ABTestVersion") as string | null,
  getPath: () => (typeof window !== "undefined" ? window.location.pathname : "/"),
  getDeviceType,
  getEntryContext,
  getConsent: analyticsConsentState,
  getNavigationType: () => navigationTypeOf(),
  uuid: newClientEventId,
  session: storage("sessionStorage"),
  local: storage("localStorage"),
});

export const track = <K extends EventName>(
  name: K,
  payload: EventPayloads[K],
  opts?: TrackOptions
): void => tracker.track(name, payload, opts);

/**
 * `field_invalid`: un campo del paso actual pasa a tener error. `code`: motivo
 * corto tipo slug si se sabe (p. ej. el `code` de Stripe Elements), nunca el texto.
 */
export const trackValidationError = (
  field: string,
  errorType: ValidationErrorType = "format",
  code: string | null = null
): void => tracker.fieldInvalid(field, errorType, code);

/**
 * `step_submit_failed`: falla el envío del paso actual. El tipo se deduce del
 * error (409 → conflict, otro status → status, sin respuesta → network,
 * Stripe → stripe); `override` lo fija (`signature`, `proposal`). Un fallo de
 * pago NO va aquí: va solo como `trackPaymentFailed`.
 */
export const trackSubmissionError = (
  error: unknown,
  override?: { error_type?: SubmissionErrorType; code?: string | number | null }
): void => tracker.stepSubmitFailed(error, override);

export const trackQuestionViewed = (questionId: string | number, stack: Stack): void =>
  tracker.questionViewed(questionId, stack);

export const trackQuestionAnswered = (questionId: string | number, stack: Stack): void =>
  tracker.questionAnswered(questionId, stack);

/** Elige un método en el selector. */
export const trackPaymentMethodSelected = (method: PaymentMethod): void =>
  tracker.paymentMethodSelected(method);

/** Envía el método (guardar tarjeta o pagar con wallet). */
export const trackPaymentMethodSubmitted = (method: PaymentMethod): void =>
  tracker.paymentMethodSubmitted(method);

/** Stripe y el backend han guardado el método. */
export const trackPaymentMethodSaved = (method: PaymentMethod): void =>
  tracker.paymentMethodSaved(method);

export const trackPaymentFailed = (
  stage: PaymentStage,
  error: unknown,
  method?: PaymentMethod | null
): void => tracker.paymentFailed(stage, error, method);

export const trackThreeDsRequested = (stage: PaymentStage): void =>
  tracker.threeDsRequested(stage);

export const trackThreeDsCompleted = (stage: PaymentStage, result: ThreeDsResult): void =>
  tracker.threeDsCompleted(stage, result);

export const trackCta = (ctaId: CtaId): void => tracker.ctaClicked(ctaId);

export const trackHelpRequested = (channel: HelpChannel): void =>
  tracker.helpRequested(channel);

export const trackContentOpened = (contentId: string): void =>
  tracker.contentOpened(contentId);

/** Antes de cambiar el lead: cómo lo conoce el front (`lead_identified.via`). */
export const expectLeadIdentification = (via: LeadVia): void =>
  tracker.expectIdentification(via);

export const trackLoginCodeRequested = (): void => tracker.loginCodeRequested();

export const trackLoginSucceeded = (method: LoginMethod): void =>
  tracker.loginSucceeded(method);

export const trackLoginFailed = (method: LoginMethod, error: unknown): void =>
  tracker.loginFailed(method, error);
